import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/PageHeader/PageHeader";
import { IntegrationsSection } from "@/components/IntegrationsSection/IntegrationsSection";
import "./IntegrationsPage.scss";
import { IntegrationConnectionCard } from "@/components/IntegrationConnectionCard/IntegrationConnectionCard";

import { ReactComponent as TeamIcon } from "@/assets/icons/team.svg";
import { ReactComponent as BillsIcon } from "@/assets/icons/bills.svg";
import { ReactComponent as SalesforceIcon } from "@/assets/icons/connections/salesforce.svg";
import { ReactComponent as HubspotIcon } from "@/assets/icons/connections/hubspot.svg";
import { ReactComponent as StripeIcon } from "@/assets/icons/connections/stripe.svg";
import { ReactComponent as QontoIcon } from "@/assets/icons/connections/qonto.svg";
import { ReactComponent as NetsuiteIcon } from "@/assets/icons/connections/netsuite.svg";
import { ReactComponent as CsvIcon } from "@/assets/icons/csv.svg";
import { getConnections } from "@/api/user";
import {
  ConnectionProvider,
  isSalesConnectionProvider,
  UserConnections,
} from "@/types/ConnectionProvider";
import {
  handleSalesforceConnection,
  handleHubspotConnection,
  handleStripeConnection,
  handleQontoConnection,
} from "./integration-connection-utils";
import { Loader } from "@/components/Loader/Loader";
import { GenericErrorMessage } from "@/components/GenericErrorMessage/GenericErrorMessage";
import { CsvSalesImportModal } from "@/pages/IntegrationsPage/CsvSalesImportModal/CsvSalesImportModal";

export const IntegrationsPage: React.FC = () => {
  const { t } = useTranslation();
  const unavailableProviders = ["NETSUITE"];

  const [connections, setConnections] = useState<UserConnections>({
    salesConnectionProvider: null,
    billingConnectionProvider: null,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [isCsvSalesImportModalOpen, setIsCsvSalesImportModalOpen] =
    useState(false);

  useEffect(() => {
    setIsLoading(true);
    setIsError(false);

    getConnections()
      .then((data) => {
        setConnections(data);
      })
      .catch((error) => {
        setIsError(true);
      })
      .finally(() => {
        setTimeout(() => {
          setIsLoading(false);
        }, 500);
      });
  }, []);

  const computeStatus = (
    userConnections: UserConnections,
    connectionProvider: ConnectionProvider,
  ) => {
    if (unavailableProviders.includes(connectionProvider)) {
      return "unavailable";
    }
    const currentConnectionProvider = isSalesConnectionProvider(
      connectionProvider,
    )
      ? userConnections.salesConnectionProvider
      : userConnections.billingConnectionProvider;

    if (currentConnectionProvider === null) {
      return "not-connected";
    }
    if (currentConnectionProvider === connectionProvider) {
      return "connected";
    }

    return "other-tool-connected";
  };

  return (
    <>
      {isLoading && <Loader />}
      <CsvSalesImportModal
        isOpen={isCsvSalesImportModalOpen}
        onClose={() => setIsCsvSalesImportModalOpen(false)}
      />

      <div className="integrations-page">
        <PageHeader
          title={t("pages.integrations.title")}
          subtitle={t("pages.integrations.subtitle")}
        />

        {isError && <GenericErrorMessage />}

        {!isLoading && !isError && (
          <div className="integrations-page-sections">
            <IntegrationsSection
              icon={<TeamIcon aria-hidden="true" focusable="false" />}
              title={t("pages.integrations.sections.crmSales")}
            >
              <IntegrationConnectionCard
                icon={<SalesforceIcon />}
                name={t("pages.integrations.connections.salesforce.name")}
                status={computeStatus(connections, "SALESFORCE")}
                onAction={() => handleSalesforceConnection()}
              />

              <IntegrationConnectionCard
                icon={<HubspotIcon />}
                name={t("pages.integrations.connections.hubspot.name")}
                status={computeStatus(connections, "HUBSPOT")}
                onAction={() => handleHubspotConnection()}
              />

              <IntegrationConnectionCard
                icon={<CsvIcon aria-hidden="true" focusable="false" />}
                name={t("pages.integrations.csvImport.cardTitle")}
                status="not-connected"
                hideStatus
                onAction={() => setIsCsvSalesImportModalOpen(true)}
              />
            </IntegrationsSection>

            <IntegrationsSection
              icon={<BillsIcon aria-hidden="true" focusable="false" />}
              title={t("pages.integrations.sections.billingErp")}
            >
              <IntegrationConnectionCard
                icon={<StripeIcon />}
                name={t("pages.integrations.connections.stripe.name")}
                status={computeStatus(connections, "STRIPE")}
                onAction={() => handleStripeConnection()}
              />

              <IntegrationConnectionCard
                icon={<QontoIcon />}
                name={t("pages.integrations.connections.qonto.name")}
                status={computeStatus(connections, "QONTO")}
                onAction={() => handleQontoConnection()}
              />

              <IntegrationConnectionCard
                icon={<NetsuiteIcon />}
                name={t("pages.integrations.connections.netsuite.name")}
                status={computeStatus(connections, "NETSUITE")}
                onAction={() => undefined}
              />
            </IntegrationsSection>
          </div>
        )}
      </div>
    </>
  );
};
